import type { UIMessageChunk } from 'ai';

import { QueuedConversationTransportHelper } from './queued-conversation-transport-helper';
import type {
  AgentConversationDataPart,
  AgentConversationEnvelopeMessage,
  AgentConversationMessagePart,
  AgentConversationTextPart,
  AgentReplyChunkController,
} from './types';

/** Turns successive reads of one reply into the chunks the AI SDK expects. On a resume the drawn
 * parts are already on screen, so only what is new or changed is emitted. */
export class ReplyChunkEmitter {
  private started = false;

  private reportedError = false;

  private seenParts: number;

  private readonly drawnPartCount: number;

  private readonly emittedToolOutputs = new Set<string>();

  private readonly openTextParts = new Map<number, 'text' | 'reasoning'>();

  private readonly sentTextLength = new Map<number, number>();

  private readonly sentData = new Map<number, string>();

  constructor(
    private readonly controller: AgentReplyChunkController,
    drawn: AgentConversationEnvelopeMessage | null
  ) {
    const drawnParts = drawn?.parts ?? [];
    this.drawnPartCount = drawnParts.length;
    this.seenParts = drawnParts.length;
    drawnParts.forEach((part, index) => {
      if (!part) return;
      const toolCallId = QueuedConversationTransportHelper.toolCallIdOf(part);
      if (
        toolCallId !== null &&
        QueuedConversationTransportHelper.isSettledToolPart(part)
      )
        this.emittedToolOutputs.add(toolCallId);
      if (QueuedConversationTransportHelper.isDataPart(part))
        this.sentData.set(index, JSON.stringify(part.data));
    });
  }

  emitReply(reply: AgentConversationEnvelopeMessage): void {
    if (!this.started) {
      this.controller.enqueue({ type: 'start', messageId: reply.messageId });
      this.started = true;
    }

    const parts = reply.parts ?? [];
    parts.forEach((part, index) => {
      if (part) this.emitPart(part, index, index >= this.seenParts);
    });
    this.seenParts = Math.max(this.seenParts, parts.length);

    if (reply.errorMessage && !this.reportedError) {
      this.controller.enqueue({ type: 'error', errorText: reply.errorMessage });
      this.reportedError = true;
    }
  }

  close(finalChunks: UIMessageChunk[]): void {
    this.openTextParts.forEach((kind, index) =>
      this.controller.enqueue(
        kind === 'text'
          ? { type: 'text-end', id: `${index}` }
          : { type: 'reasoning-end', id: `${index}` }
      )
    );
    this.openTextParts.clear();
    finalChunks.forEach((chunk) => this.controller.enqueue(chunk));
    this.controller.close();
  }

  private emitPart(
    part: AgentConversationMessagePart,
    index: number,
    isNew: boolean
  ): void {
    if (part.type === 'text' || part.type === 'reasoning') {
      this.emitText(part, index, isNew);
      return;
    }
    if (QueuedConversationTransportHelper.isDataPart(part)) {
      this.emitData(part, index, isNew);
      return;
    }

    const chunks = isNew
      ? QueuedConversationTransportHelper.partToChunks(part)
      : [this.unsentToolOutput(part)].filter(
          (chunk): chunk is UIMessageChunk => chunk !== null
        );
    chunks.forEach((chunk) => this.controller.enqueue(chunk));

    const toolCallId = QueuedConversationTransportHelper.toolCallIdOf(part);
    if (
      toolCallId !== null &&
      QueuedConversationTransportHelper.isSettledToolPart(part)
    )
      this.emittedToolOutputs.add(toolCallId);
  }

  /** A part already emitted can still gain its tool output, which is why the output is tracked by
   * call id rather than by position alone. */
  private unsentToolOutput(
    part: AgentConversationMessagePart
  ): UIMessageChunk | null {
    const toolCallId = QueuedConversationTransportHelper.toolCallIdOf(part);
    if (toolCallId === null || this.emittedToolOutputs.has(toolCallId))
      return null;

    return QueuedConversationTransportHelper.toolOutputChunk(part);
  }

  /** The builder rewrites one text block as it streams, so a part grows in place between reads and
   * only its new suffix is sent. A drawn part has no open part in the SDK to extend. */
  private emitText(
    part: AgentConversationTextPart,
    index: number,
    isNew: boolean
  ): void {
    const id = `${index}`;
    if (isNew) {
      this.controller.enqueue(
        part.type === 'text'
          ? { type: 'text-start', id }
          : { type: 'reasoning-start', id }
      );
      this.openTextParts.set(index, part.type);
    }

    const kind = this.openTextParts.get(index);
    const sent = this.sentTextLength.get(index) ?? 0;
    if (kind === undefined || part.text.length <= sent) return;

    const delta = part.text.slice(sent);
    this.controller.enqueue(
      kind === 'text'
        ? { type: 'text-delta', id, delta }
        : { type: 'reasoning-delta', id, delta }
    );
    this.sentTextLength.set(index, part.text.length);
  }

  /** The SDK replaces a data part it already holds by type and id, so a changed one is sent again
   * under a stable id; a drawn part without an id could only be duplicated. */
  private emitData(
    part: AgentConversationDataPart,
    index: number,
    isNew: boolean
  ): void {
    const signature = JSON.stringify(part.data);
    if (this.sentData.get(index) === signature) return;
    if (!isNew && !part.id && index < this.drawnPartCount) return;

    this.controller.enqueue({
      type: part.type,
      id: part.id || `part-${index}`,
      data: part.data,
    } as UIMessageChunk);
    this.sentData.set(index, signature);
  }
}
